export var ConditionType;
(function (ConditionType) {
    ConditionType["LessThan"] = "LESS_THAN";
    ConditionType["LessThanOrEqual"] = "LESS_THAN_OR_EQUAL";
    ConditionType["GreaterThan"] = "GREATER_THAN";
    ConditionType["GreaterThanOrEqual"] = "GREATER_THAN_OR_EQUAL";
    ConditionType["Equal"] = "EQUAL";
    ConditionType["NotEqual"] = "NOT_EQUAL";
    ConditionType["Between"] = "BETWEEN";
    ConditionType["NotBetween"] = "NOT_BETWEEN";
    ConditionType["In"] = "IN";
    ConditionType["NotIn"] = "NOT_IN";
    ConditionType["And"] = "AND";
    ConditionType["Or"] = "OR";
    ConditionType["Null"] = "NULL";
    ConditionType["NotNull"] = "NOT_NULL";
    ConditionType["Not"] = "NOT";
})(ConditionType || (ConditionType = {}));
export var ClosedType;
(function (ClosedType) {
    ClosedType["Left"] = "LEFT";
    ClosedType["Right"] = "RIGHT";
    ClosedType["Both"] = "BOTH";
    ClosedType["Neither"] = "NEITHER";
})(ClosedType || (ClosedType = {}));
export class ConditionUtils {
    static getDisplayNameForType(type) {
        switch (type) {
            case ConditionType.LessThan:
                return "Is Less Than";
            case ConditionType.LessThanOrEqual:
                return "Is Less Than Or Equal To";
            case ConditionType.GreaterThan:
                return "Is Greater Than";
            case ConditionType.GreaterThanOrEqual:
                return "Is Greater Than Or Equal To";
            case ConditionType.Equal:
                return "Is Equal To";
            case ConditionType.NotEqual:
                return "Is Not Equal To";
            case ConditionType.Between:
                return "Is Between";
            case ConditionType.NotBetween:
                return "Is Not Between";
            case ConditionType.In:
                return "Is In";
            case ConditionType.NotIn:
                return "Is Not In";
            case ConditionType.And:
                return "And";
            case ConditionType.Or:
                return "Or";
            case ConditionType.Null:
                return "Is Null";
            case ConditionType.NotNull:
                return "Is Not Null";
            case ConditionType.Not:
                return "Is Not";
            default:
                throw new Error(`Unrecognized Type: ${type}`);
        }
    }
    static getTypeFromDisplayName(name) {
        switch (name) {
            case "Is Less Than":
                return ConditionType.LessThan;
            case "Is Less Than Or Equal To":
                return ConditionType.LessThanOrEqual;
            case "Is Greater Than":
                return ConditionType.GreaterThan;
            case "Is Greater Than Or Equal To":
                return ConditionType.GreaterThanOrEqual;
            case undefined: // default
            case "Is Equal To":
                return ConditionType.Equal;
            case "Is Not Equal To":
                return ConditionType.NotEqual;
            case "Is Between":
                return ConditionType.Between;
            case "Is Not Between":
                return ConditionType.NotBetween;
            case "Is In":
                return ConditionType.In;
            case "Is Not In":
                return ConditionType.NotIn;
            case "And":
                return ConditionType.And;
            case "Or":
                return ConditionType.Or;
            case "Is Null":
                return ConditionType.Null;
            case "Is Not Null":
                return ConditionType.NotNull;
            case "Is Not":
                return ConditionType.Not;
            default:
                throw new Error(`Unrecognized Name: ${name}`);
        }
    }
    static humanize(condition) {
        if (condition === undefined) {
            return "";
        }
        const opLabel = ConditionUtils.getDisplayNameForType(condition.type).toLowerCase();
        if (ConditionUtils.isComparison(condition)) {
            const value = ConditionUtils.humanizeValue(condition.value);
            return `${condition.column} ${opLabel} ${value}`;
        }
        else if (ConditionUtils.isInterval(condition)) {
            const left = ConditionUtils.humanizeValue(condition.left);
            const right = ConditionUtils.humanizeValue(condition.right);
            switch (condition.closed) {
                case ClosedType.Both:
                    return `${condition.column} ${opLabel} ${left} and ${right}, inclusive`;
                case ClosedType.Left:
                    return `${condition.column} ${opLabel} ${left} and ${right}, open on the left`;
                case ClosedType.Right:
                    return `${condition.column} ${opLabel} ${left} and ${right}, open on the right`;
                case ClosedType.Neither:
                    return `${condition.column} ${opLabel} ${left} and ${right}, exclusive`;
            }
        }
        else if (ConditionUtils.isContainment(condition)) {
            const value = condition.values.map((value) => ConditionUtils.humanizeValue(value)).join(", ");
            return `${condition.column} ${opLabel} ${value}`;
        }
        else if (ConditionUtils.isCollection(condition)) {
            let delegates = condition.conditions.map((c) => ConditionUtils.humanize(c));
            if (condition.conditions.length > 1 &&
                condition.conditions.some((c) => ConditionUtils.isCollection(c) || ConditionUtils.isNot(c))) {
                delegates = delegates.map((x) => `(${x})`);
            }
            return delegates.join(` ${opLabel} `);
        }
        else if (ConditionUtils.isNull(condition)) {
            return `${condition.column} ${opLabel}`;
        }
        else if (ConditionUtils.isNot(condition)) {
            const delegate = ConditionUtils.humanize(condition.condition);
            return `(${delegate}) ${opLabel} true`;
        }
        else {
            return "";
        }
    }
    static humanizeValue(value) {
        if (value === undefined) {
            return "null";
        }
        else if (ConditionUtils.isDerivedValue(value)) {
            return value.column;
        }
        else if (ConditionUtils.isString(value)) {
            return value;
        }
        else {
            return String(value);
        }
    }
    static isColumn(condition) {
        return "column" in condition;
    }
    static isComparison(condition) {
        return ConditionUtils.isComparisonType(condition.type);
    }
    static isComparisonType(type) {
        switch (type) {
            case ConditionType.LessThan:
            case ConditionType.LessThanOrEqual:
            case ConditionType.GreaterThan:
            case ConditionType.GreaterThanOrEqual:
            case ConditionType.Equal:
            case ConditionType.NotEqual:
                return true;
            default:
                return false;
        }
    }
    static isInterval(condition) {
        return ConditionUtils.isIntervalType(condition.type);
    }
    static isIntervalType(type) {
        switch (type) {
            case ConditionType.Between:
            case ConditionType.NotBetween:
                return true;
            default:
                return false;
        }
    }
    static isContainment(condition) {
        return ConditionUtils.isContainmentType(condition.type);
    }
    static isContainmentType(type) {
        switch (type) {
            case ConditionType.In:
            case ConditionType.NotIn:
                return true;
            default:
                return false;
        }
    }
    static isCollection(condition) {
        return ConditionUtils.isCollectionType(condition.type);
    }
    static isCollectionType(type) {
        switch (type) {
            case ConditionType.And:
            case ConditionType.Or:
                return true;
            default:
                return false;
        }
    }
    static isNull(condition) {
        switch (condition.type) {
            case ConditionType.Null:
            case ConditionType.NotNull:
                return true;
            default:
                return false;
        }
    }
    static isNullType(type) {
        switch (type) {
            case ConditionType.Null:
            case ConditionType.NotNull:
                return true;
            default:
                return false;
        }
    }
    static isNot(condition) {
        switch (condition.type) {
            case ConditionType.Not:
                return true;
            default:
                return false;
        }
    }
    static hasSameType(a, b) {
        return (a.type === b.type ||
            (this.isComparison(a) && this.isComparison(b)) ||
            (this.isInterval(a) && this.isInterval(b)) ||
            (this.isContainment(a) && this.isContainment(b)) ||
            (this.isCollection(a) && this.isCollection(b)) ||
            (this.isNull(a) && this.isNull(b)) ||
            (this.isNot(a) && this.isNot(b)));
    }
    static isValidColumn(column) {
        return column !== undefined && column !== "";
    }
    static isInvalidColumn(column) {
        return !ConditionUtils.isValidColumn(column);
    }
    static isValidExpression(expression) {
        if (expression === undefined) {
            return false;
        }
        else if (ConditionUtils.isDerivedValue(expression)) {
            return ConditionUtils.isValidColumn(expression.column);
        }
        return true;
    }
    static isValid(condition) {
        if (condition === undefined || condition.type === undefined) {
            return false;
        }
        else if (ConditionUtils.isComparison(condition)) {
            return ConditionUtils.isValidColumn(condition.column) && ConditionUtils.isValidExpression(condition.value);
        }
        else if (ConditionUtils.isInterval(condition)) {
            return (ConditionUtils.isValidColumn(condition.column) &&
                ConditionUtils.isValidExpression(condition.left) &&
                ConditionUtils.isValidExpression(condition.right));
        }
        else if (ConditionUtils.isContainment(condition)) {
            return (ConditionUtils.isValidColumn(condition.column) && condition.values !== undefined && condition.values.length > 0);
        }
        else if (ConditionUtils.isCollection(condition)) {
            return (condition.conditions !== undefined &&
                condition.conditions.length > 0 &&
                condition.conditions.every((c) => ConditionUtils.isValid(c)));
        }
        else if (ConditionUtils.isNull(condition)) {
            return ConditionUtils.isValidColumn(condition.column);
        }
        else if (ConditionUtils.isNot(condition)) {
            return condition.condition !== undefined && ConditionUtils.isValid(condition);
        }
        else {
            return false;
        }
    }
    static removeInvalid(conditions) {
        return conditions
            .map((condition) => {
            if (ConditionUtils.isCollection(condition)) {
                return {
                    ...condition,
                    conditions: ConditionUtils.removeInvalid(condition.conditions || [])
                };
            }
            else {
                return condition;
            }
        })
            .filter((condition) => ConditionUtils.isValid(condition));
    }
    static isNumber(x) {
        return typeof x === "number";
    }
    static isString(x) {
        return typeof x === "string";
    }
    static isDerivedValue(x) {
        return x !== undefined && typeof x === "object" && "column" in x;
    }
    static isExpressionEqual(a, b) {
        if (this.isDerivedValue(a)) {
            return this.isDerivedValue(b) && a.column === b.column;
        }
        return a === b;
    }
    static isEqual(a, b) {
        if (a === undefined || b === undefined) {
            return a === b;
        }
        else if (ConditionUtils.isComparison(a)) {
            return (ConditionUtils.isComparison(b) &&
                a.type === b.type &&
                a.column === b.column &&
                ConditionUtils.isExpressionEqual(a, b));
        }
        else if (ConditionUtils.isInterval(a)) {
            return (ConditionUtils.isInterval(b) &&
                a.type == b.type &&
                a.column === b.column &&
                a.closed === b.closed &&
                ConditionUtils.isExpressionEqual(a.left, b.left) &&
                ConditionUtils.isExpressionEqual(a.right, b.right));
        }
        else if (ConditionUtils.isContainment(a)) {
            return (ConditionUtils.isContainment(b) &&
                a.type === b.type &&
                a.column === b.column &&
                a.values.length === b.values.length &&
                a.values.every((_, i) => a.values[i] === b.values[i]));
        }
        else if (ConditionUtils.isCollection(a)) {
            return (ConditionUtils.isCollection(b) &&
                a.type === b.type &&
                a.conditions.length === b.conditions.length &&
                a.conditions.every((_, i) => ConditionUtils.isEqual(a.conditions[i], b.conditions[i])));
        }
        else if (ConditionUtils.isNull(a)) {
            return ConditionUtils.isNull(b) && a.type === b.type && a.column === b.column;
        }
        else if (ConditionUtils.isNot(a)) {
            return ConditionUtils.isNot(b) && a.type === b.type && ConditionUtils.isEqual(a.condition, b.condition);
        }
        else {
            return false;
        }
    }
    static getColumnReferences(c) {
        if (c === undefined) {
            return [];
        }
        if (ConditionUtils.isComparison(c)) {
            return [{ column: c.column }].concat(ConditionUtils.isDerivedValue(c.value) ? [c.value] : []);
        }
        else if (ConditionUtils.isInterval(c)) {
            return [{ column: c.column }]
                .concat(ConditionUtils.isDerivedValue(c.left) ? [c.left] : [])
                .concat(ConditionUtils.isDerivedValue(c.right) ? [c.right] : []);
        }
        else if (ConditionUtils.isContainment(c)) {
            return [{ column: c.column }];
        }
        else if (ConditionUtils.isCollection(c)) {
            return c.conditions.flatMap(ConditionUtils.getColumnReferences);
        }
        else if (ConditionUtils.isNull(c)) {
            return [{ column: c.column }];
        }
        else if (ConditionUtils.isNot(c)) {
            return ConditionUtils.getColumnReferences(c);
        }
        else {
            return [];
        }
    }
    static getDefaultColumnCondition(type, column, fill) {
        if (ConditionUtils.isComparisonType(type)) {
            return {
                type: type,
                column: column,
                value: fill
            };
        }
        else if (ConditionUtils.isContainmentType(type)) {
            return {
                type: type,
                column: column,
                values: []
            };
        }
        else if (ConditionUtils.isIntervalType(type)) {
            return {
                type: type,
                column: column,
                left: fill,
                right: fill,
                closed: ClosedType.Both
            };
        }
        else if (ConditionUtils.isNullType(type)) {
            return {
                type: type,
                column: column
            };
        }
        throw new Error(`unknown type: ${type}`);
    }
}
